<template>
  <div class="chat-wrapper">
    <Card class="chat-container p-no-shadow-app">
      <template #content>
        <div class="chat-layout">
          <div ref="scrollContainer" class="chat-messages p-p-3">
            <div
              v-for="message in messages"
              :key="message.id"
              :class="['p-d-flex', 'p-mb-3', message.isClient ? 'p-jc-start' : 'p-jc-end']"
            >
              <div
                :class="[
                  'chat-bubble',
                  message.isClient ? 'chat-bubble--client' : 'chat-bubble--support'
                ]"
              >
                <p class="p-m-0 p-mb-2">{{ message.message }}</p>

                <div class="p-d-flex p-flex-column p-ai-end p-text-secondary">
                  <span class="p-text-xs">{{ formatTimestamp(message.time) }}</span>
                  <i
                    v-if="!message.isClient"
                    :class="statusIconClass(message)"
                    v-tooltip.bottom="statusIconLegend(message)"
                  />
                </div>

                 <div
                  v-if="!message.isClient && message.userId !== currentUserId"
                  class="p-d-flex p-jc-end p-text-light chat-user"
                >
                  <small>{{ message.userName }}</small>
                </div>
              </div>
            </div>
          </div>

          <div v-if="can('sendMessageInPatChat')" class="chat-input p-p-3" style="width: 100%;">
            <div class="p-d-flex p-ai-stretch" style="width: 100%;">
              <Textarea
                v-model="draft"
                autoResize
                rows="3"
                class="p-flex-1"
                style="flex: 1; width: 100%;"
                placeholder="Escreva uma mensagem..."
              />
              <Button
                label="Enviar"
                icon="pi pi-send"
                class="p-ml-3 chat-send-button"
                iconPos="right"
                :disabled="isSendDisabled"
                @click="sendMessage"
              />
            </div>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import Card from 'primevue/card';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import Tooltip from 'primevue/tooltip';
import ChatService from '../../services/chat.service'

export default {
  name: 'Chat',
  components: {
    Card,
    Button,
    Textarea,
  },
  directives: {
    tooltip: Tooltip
  },

  props: {
    patId: { type: Number, default: null }
  },

  data () {
    return {
      messages: [],
      draft: '',
      loading: false,
      lastId: 0,
      eventSource: null,
      reconnectTimer: null,
      refreshTimer: null,
      REFRESH_INTERVAL_MS: 5 * 60 * 1000
    }
  },

  computed: {
    isSendDisabled () {
      if (!this.draft.trim() || this.loading || !this.patId) {
        return true
      }

      return false
    },

    currentUserId () {
      const store = this.$store
      if (!store || !store.state || !store.state.auth || !store.state.auth.user) {
        return null
      }
      return store.state.auth.user.id
    },
  },

  watch: {
    patId: {
      immediate: true,
      handler (newPatId) {
        this.stopRefreshTimer()
        this.getMessages(newPatId).finally(() => {
          if (newPatId) {
            this.startRefreshTimer()
          }
        })
      }
    }
  },

  beforeDestroy () {
    this.closeStream()
    this.stopRefreshTimer()
  },

  methods: {
    async getMessages (patId) {
      this.closeStream()
      if (!patId) {
        this.messages = []
        this.lastId = 0
        return
      }

      this.loading = true
      try {
        const response = await ChatService.getMessages(patId)
        this.messages = Array.isArray(response) ? response : []

        if (this.messages.length) {
          const last = this.messages[this.messages.length - 1]
          this.lastId = last && last.id != null ? last.id : 0
        } else {
          this.lastId = 0
        }
      } catch (error) {
        this.$toast.add({ severity: 'error', summary: 'Erro ao carregar mensagens.', life: 4000 })
      } finally {
        await this.markAsSeen(patId, this.lastId)
        this.loading = false
        this.scrollToBottom()
        this.openStream(patId)
      }
    },

    async markAsSeen (pat, id) {
      if (!pat || !id) {
        return
      }
      try {
        await ChatService.markAsSeen(pat, id)
      } catch (error) {
        this.$toast.add({ severity: 'error', summary: 'Erro ao marcar mensagens como vistas.', life: 4000 })
      }
    },
    async sendMessage () {
      const text = this.draft.trim()
      if (!text || !this.patId) {
        return
      }
      this.loading = true
      try {
        await ChatService.newMessage(this.patId, text)
      } catch (error) {
        this.$toast.add({ severity: 'error', summary: 'Erro ao enviar mensagem.', life: 4000 })
      } finally {
        this.draft = ''
        this.loading = false
      }
    },
    async refreshMessages () {
      if (!this.patId) {
        return
      }
      try {
        const response = await ChatService.getMessages(this.patId)
        if (!Array.isArray(response)) {
          return
        }

        let highestId = Number(this.lastId) || 0
        response.forEach(row => {
          if (!row || row.id == null) {
            return
          }
          const idx = this.messages.findIndex(m => m && String(m.id) === String(row.id))
          if (idx !== -1) {
            this.$set(this.messages, idx, { ...this.messages[idx], ...row })
          } else {
            this.messages.push(row)
          }

          const n = Number(row.id)
          if (!Number.isNaN(n) && n > highestId) {
            highestId = n
          }
        })
        if (highestId) this.lastId = highestId
      } catch (error) {
        this.$toast.add({ severity: 'error', summary: 'Erro ao buscar novas mensagens.', life: 4000 })
      }
    },

    startRefreshTimer () {
      this.stopRefreshTimer()
      this.refreshTimer = setInterval(() => this.refreshMessages(), this.REFRESH_INTERVAL_MS)
    },

    stopRefreshTimer () {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    appendMessages (rows) {
      const incoming = Array.isArray(rows) ? rows : [rows]
      if (!incoming.length) {
        return
      }

      let highestId = Number(this.lastId) || 0

      incoming.forEach(row => {
        if (!row || row.id == null) {
          return
        }

        const idx = this.messages.findIndex(m => m && String(m.id) === String(row.id))
        if (idx !== -1) {
          this.$set(this.messages, idx, { ...this.messages[idx], ...row })
        } else {
          this.messages.push(row)
        }

        const n = Number(row.id)
        if (!Number.isNaN(n) && n > highestId) {
          highestId = n
        }
      })

      if (highestId) {
        this.lastId = highestId
      }
      this.scrollToBottom()
    },

    openStream (patId) {
      if (!patId) {
        return
      }
      const controller = new AbortController()
      this.eventSource = controller
      this.pollStream(patId)
    },

    closeStream () {
      if (this.eventSource && this.eventSource.abort) {
        this.eventSource.abort()
        this.eventSource = null
      }
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
    },

    async pollStream (patId) {
      try {
        const response = await ChatService.openStream(patId, this.lastId, {
          signal: this.eventSource ? this.eventSource.signal : undefined
        })
        if (response) {
          this.handleIncomingMessage(response)
        }
        if (this.eventSource && !this.eventSource.signal.aborted) {
          this.scheduleNextPoll(patId)
        }
      } catch (error) {
        this.handleStreamError(error)
      }
    },

    handleStreamError (error) {
      if (error) {
        console.error('[Chat] Stream encerrado com erro:', error)
      }
      this.closeStream()
      this.scheduleReconnect(this.patId)
    },

    scheduleReconnect (patId) {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
      }
      this.reconnectTimer = window.setTimeout(() => this.openStream(patId), 3000)
    },

    scheduleNextPoll (patId) {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
      }
      this.reconnectTimer = window.setTimeout(() => this.pollStream(patId), 2000)
    },

    handleIncomingMessage (eventOrPayload) {
      if (eventOrPayload == null) {
        return
      }
      let payload = eventOrPayload && eventOrPayload.data !== undefined
        ? eventOrPayload.data
        : eventOrPayload
      if (payload == null) {
        return
      }
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (error) {
          console.error(error)
          return
        }
      }

      if (Array.isArray(payload)) {
        this.appendMessages(payload)
        return
      }

      if (payload && typeof payload === 'object') {
        const groups = [payload.messages, payload.rows, payload.data].filter(Array.isArray)
        if (groups.length) {
          groups.forEach(arr => this.appendMessages(arr))
          const pid = Number(payload.last_id || payload.lastId)
          if (!Number.isNaN(pid) && pid > Number(this.lastId || 0)){
            this.lastId = pid
          }
          return
        }
        this.appendMessages(payload)
        const pid = Number(payload.last_id || payload.lastId)
        if (!Number.isNaN(pid) && pid > Number(this.lastId || 0)) {
          this.lastId = pid
        }
      }
    },

    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.scrollContainer
        if (el) {
          el.scrollTop = el.scrollHeight
        }
      })
    },

    isMessageSeen (message) {
      if (message && message.isRead) {
        return true
      }
      return false
    },

    statusIconClass (message) {
      if (!message || message.isClient) {
        return ''
      }

      if (this.isMessageSeen(message)) {
        return 'pi pi-check-circle p-text-primary'
      }
      return 'pi pi-check p-text-secondary'
    },

    statusIconLegend (message) {
      if (!message || message.isClient){
        return ''
      }

      if (this.isMessageSeen(message)) {
        'Vista'
      }
      return 'Entregue'
    },

    formatTimestamp (value) {
      if (!value) {
        return ''
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return value
      }
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = String(date.getFullYear()).slice(-2)
      return `${hours}:${minutes} - ${day}/${month}/${year}`
    }
  }
}
</script>

<style scoped>
.chat-wrapper {
  width: 100%;
  display: flex;
  max-height: 600px;
  min-height: 18rem;
  align-items: stretch;
}

.chat-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 600px;
  min-height: 16rem;
  overflow: hidden;
}

.chat-container :deep(.p-card-body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.chat-container :deep(.p-card-content) {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  min-height: 0;
}

.chat-layout {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  background: var(--surface-b);
  min-height: 0;
}

.chat-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #475569;
  min-height: 220px;
}

.chat-loading__icon {
  font-size: 1.5rem;
}

.chat-send-button {
  align-self: stretch;
  height: 100%;
  max-height: 100%;
}

.chat-input {
  border-top: 1px solid var(--surface-d);
  background: var(--surface-a);
  flex-shrink: 0;
}

.chat-input :deep(textarea) {
  min-height: 6rem;
}

@media (max-width: 600px) {
  .chat-input :deep(textarea) {
    min-height: 3.5rem;
  }
}

.chat-bubble {
  max-width: 70%;
  border-radius: 1rem;
  padding: 0.75rem 1rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.chat-bubble--support {
  background-color: #d7e8ff;
  color: #1f2a40;
}

.chat-bubble--client {
  background-color: #f4f4f5;
  color: #2f2f2f;
}

.chat-user {
  margin-top: 0.5rem;
}
</style>
